
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { Disclosure } from '@headlessui/react';
import { PlayIcon, PauseIcon, ChevronDownIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import Link from 'next/link';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useUser } from '@/utils/useUser';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import clsxm from '@/lib/clsxm';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import Seo from '@/components/Seo';
import { music_subpages } from '@/constants/music_genres';
const Footer = dynamic(() => import('@/components/layout/Footer'));
// Audio player states
enum AudioPlayerState {
    IDLE = 'idle',
    LOADING = 'loading',
    PLAYING = 'playing',
    PAUSED = 'paused',
    ERROR = 'error'
}
export default function AIMusicGenerator() {
    const { t } = useLocale('music-generator');
    const { user } = useUser();
    const [playerState, setPlayerState] = useState<AudioPlayerState>(AudioPlayerState.IDLE);
    const [activeDemo, setActiveDemo] = useState<number>(0);
    const [audioPosition, setAudioPosition] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const audioControlTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);
    // Function to format time (seconds to mm:ss)
    const formatTime = (seconds: number) => {
        if (!isFinite(seconds))
            return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    // Function to update progress
    const updateProgress = useCallback(() => {
        const audio = audioElementRef.current;
        if (audio && audio.duration) {
            setAudioPosition(audio.currentTime);
            setAudioDuration(audio.duration);
        }
    }, []);
    // Example music tracks with descriptions
    const demoTracks = [
        {
            name: t('Jingle and Sway'),
            description: t('Rock song with electric guitar riffs, groovy basslines, and a steady drumbeat.'),
            genre: 'Rock',
            audioUrl: 'https://api.easy-peasy.ai/storage/v1/object/public/tts/7a7cabda-b24e-47c5-a32f-9109d19ce0a8/cdbd6020-5059-4c30-96a8-cee63219894b.mp3',
            duration: '2:16',
            prompt: 'Rock song with electric guitar riffs, groovy basslines, and a steady drumbeat',
        },
        {
            name: t('Coffee Cup Karma'),
            description: t('Warm lo-fi groove with brushed drums, upright bass, and cozy electric piano.'),
            genre: 'Lo-Fi',
            audioUrl: 'https://api.easy-peasy.ai/storage/v1/object/public/tts/46bec11e-4566-43df-970b-6f2b72367616/4121ccf4-7049-4838-82c8-81fa2c001c83.mp3',
            duration: '3:09',
            prompt: 'Warm lo-fi groove with brushed drums, upright bass, and cozy electric piano',
        },
        {
            name: t('Dancing Is My Love'),
            description: t('Synth-pop with heavy 808s and driving beat, catchy and danceable.'),
            genre: 'Pop',
            audioUrl: 'https://api.easy-peasy.ai/storage/v1/object/public/tts/4f5bda6c-cfbb-43bf-a06a-b8bf51e3a26a/81a3fb52-9815-4de2-a132-6f88c261cf1d.mp3',
            duration: '2:05',
            prompt: 'Synth-pop with heavy 808s and driving beat, catchy and danceable',
        },
        {
            name: t('Streetlight Serenade'),
            description: t('Contemporary jazz with saxophone, harmonica, and gentle drumbeats.'),
            genre: 'Jazz',
            audioUrl: 'https://api.easy-peasy.ai/storage/v1/object/public/tts/412daa02-0ac2-41b4-be6a-89918cd663c2/5fba788a-7ed3-469d-8005-42b206abd73c.mp3',
            duration: '3:28',
            prompt: 'Contemporary jazz with saxophone, harmonica, and gentle drumbeats',
        },
    ];
    // Initialize audio element
    useEffect(() => {
        const audio = new Audio();
        audio.preload = 'auto';
        audio.crossOrigin = 'anonymous';
        audio.volume = 0.6;
        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('ended', () => {
            setPlayerState(AudioPlayerState.IDLE);
            setAudioPosition(0);
        });
        audio.addEventListener('loadedmetadata', () => {
            setAudioDuration(audio.duration);
        });
        audioElementRef.current = audio;
        return () => {
            audio.pause();
            audio.removeEventListener('timeupdate', updateProgress);
            audioElementRef.current = null;
        };
    }, [updateProgress]);
    // Track selection handler
    const handleTrackSelect = useCallback((index: number) => {
        const audio = audioElementRef.current;
        if (!audio)
            return;
        if (playerState === AudioPlayerState.LOADING)
            return;
        setPlayerState(AudioPlayerState.LOADING);
        setActiveDemo(index);
        audio.pause();
        audio.currentTime = 0;
        audio.src = demoTracks[index].audioUrl;
        audio
            .play()
            .then(() => {
            setPlayerState(AudioPlayerState.PLAYING);
        })
            .catch((err) => {
            console.error('Error playing audio:', err);
            setPlayerState(AudioPlayerState.PAUSED);
        });
    }, [playerState, demoTracks]);
    // Toggle play/pause
    const handlePlayPause = useCallback(() => {
        const audio = audioElementRef.current;
        if (!audio)
            return;
        if (playerState === AudioPlayerState.PLAYING) {
            audio.pause();
            setPlayerState(AudioPlayerState.PAUSED);
        }
        else {
            if (!audio.src) {
                audio.src = demoTracks[activeDemo].audioUrl;
            }
            audio
                .play()
                .then(() => {
                setPlayerState(AudioPlayerState.PLAYING);
            })
                .catch((err) => {
                console.error('Error playing audio:', err);
                setPlayerState(AudioPlayerState.PAUSED);
            });
        }
    }, [playerState, activeDemo, demoTracks]);
    // Seek handler
    const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioElementRef.current;
        if (!audio || !audio.duration)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = x / rect.width;
        audio.currentTime = percentage * audio.duration;
    }, []);
    const faqs = [
        {
            question: t('faq_q_what_is_ai_music_generator'),
            answer: t('faq_a_what_is_ai_music_generator'),
        },
        {
            question: t('faq_q_can_ai_generate_songs_with_lyrics'),
            answer: t('faq_a_can_ai_generate_songs_with_lyrics'),
        },
        {
            question: t('faq_q_what_genres_supported'),
            answer: t('faq_a_what_genres_supported'),
        },
        {
            question: t('faq_q_is_ai_music_royalty_free'),
            answer: t('faq_a_is_ai_music_royalty_free'),
        },
        {
            question: t('faq_q_how_to_create_music_with_ai'),
            answer: t('faq_a_how_to_create_music_with_ai'),
        },
        {
            question: t('faq_q_can_i_use_for_youtube_videos'),
            answer: t('faq_a_can_i_use_for_youtube_videos'),
        },
        {
            question: t('faq_q_difference_song_vs_music'),
            answer: t('faq_a_difference_song_vs_music'),
        },
        {
            question: t('faq_q_can_i_choose_vocals'),
            answer: t('faq_a_can_i_choose_vocals'),
        },
    ];
    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
    const isPlaying = playerState === AudioPlayerState.PLAYING;
    const isLoading = playerState === AudioPlayerState.LOADING;
    const progress = audioDuration > 0 ? (audioPosition / audioDuration) * 100 : 0;
    return (<>
      <Head>
        <link rel='preconnect' href='https://fonts.googleapis.com'/>
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous'/>
        <link href='https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap' rel='stylesheet'/>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}/>
      </Head>

      <style jsx global>{`
        :root {
          --music-bg-deep: #0a0e27;
          --music-bg-card: #12152e;
          --music-bg-elevated: #1a1f3a;
          --music-gold: #f59e0b;
          --music-gold-light: #fbbf24;
          --music-gold-dark: #d97706;
          --music-cream: #f5f5f0;
          --music-gray: #9ca3af;
          --music-gray-dark: #6b7280;
        }

        .font-display {
          font-family: 'Cormorant Garamond', serif;
        }

        .font-body {
          font-family:
            'Inter',
            -apple-system,
            BlinkMacSystemFont,
            sans-serif;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(0.95);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.7;
          }
          100% {
            transform: scale(0.95);
            opacity: 1;
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .animate-pulse-ring {
          animation: pulse-ring 2s ease-in-out infinite;
        }

        .delay-100 {
          animation-delay: 100ms;
        }

        .delay-200 {
          animation-delay: 200ms;
        }

        .delay-300 {
          animation-delay: 300ms;
        }

        .delay-400 {
          animation-delay: 400ms;
        }
      `}</style>

      <div className='relative bg-[var(--music-bg-deep)] font-body'>
        <Seo templateTitle={t('AI Music Generator')} description={t('Create professional-quality music in any genre with just a text prompt. From hip-hop to classical, our AI generates custom tracks for your projects in seconds.')} image={`https://easy-peasy.ai/api/og-template?title=${t('AI Music Generator')}`}/>
        <LandingPageNavigation />

        {/* Hero Section */}
        <section className='relative overflow-hidden px-6 pt-32 pb-20 lg:px-8 lg:pt-40'>
          {/* Ambient background effects */}
          <div className='absolute inset-0 overflow-hidden'>
            <div className='absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[var(--music-gold)] opacity-10 blur-3xl'></div>
            <div className='absolute top-60 -left-40 h-96 w-96 rounded-full bg-purple-600 opacity-10 blur-3xl'></div>
          </div>

          <div className='relative mx-auto max-w-7xl'>
            {/* Hero text */}
            <div className='mb-16 text-center opacity-0 animate-fade-in-up'>
              <div className='mb-6 inline-block'>
                <span className='rounded-full border border-[var(--music-gold)]/30 bg-[var(--music-gold)]/10 px-4 py-1.5 text-sm font-medium text-[var(--music-gold)]'>
                  Powered by AI
                </span>
              </div>
              <h1 className='font-display mb-6 text-6xl font-light leading-tight tracking-tight text-[var(--music-cream)] lg:text-8xl'>
                Create music
                <br />
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  from imagination
                </span>
              </h1>
              <p className='mx-auto max-w-2xl text-xl leading-relaxed text-[var(--music-gray)] lg:text-2xl'>
                Transform your ideas into professional compositions. Simply
                describe your vision, and watch AI bring it to life.
              </p>
            </div>

            {/* Premium Music Player */}
            <div className='mx-auto max-w-5xl opacity-0 animate-fade-in-up delay-200'>
              <div className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-8 shadow-2xl backdrop-blur-xl lg:p-12'>
                {/* Decorative elements */}
                <div className='absolute -top-24 -right-24 h-48 w-48 rounded-full bg-[var(--music-gold)] opacity-5 blur-3xl'></div>
                <div className='absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-purple-500 opacity-5 blur-3xl'></div>

                {/* Player Header */}
                <div className='relative mb-8 text-center'>
                  <div className='mb-3 inline-flex items-center gap-2'>
                    <div className='h-2 w-2 rounded-full bg-[var(--music-gold)] animate-pulse'></div>
                    <span className='text-sm font-medium uppercase tracking-wider text-[var(--music-gold)]'>
                      {isPlaying
            ? 'Now Playing'
            : isLoading
                ? 'Loading'
                : 'Ready to Play'}
                    </span>
                  </div>
                  <h2 className='font-display text-3xl font-semibold text-[var(--music-cream)] lg:text-4xl'>
                    {demoTracks[activeDemo].name}
                  </h2>
                  <p className='mt-2 text-[var(--music-gray)]'>
                    {demoTracks[activeDemo].genre}
                  </p>
                </div>

                {/* Vinyl Record Player */}
                <div className='relative mb-8 flex justify-center'>
                  <div className='relative'>
                    {/* Vinyl record */}
                    <div className={`relative h-64 w-64 rounded-full bg-gradient-to-br from-gray-900 via-gray-800 to-black shadow-2xl ${isPlaying ? 'animate-spin-slow' : ''}`}>
                      {/* Record grooves */}
                      <div className='absolute inset-4 rounded-full border-2 border-gray-700 opacity-30'></div>
                      <div className='absolute inset-8 rounded-full border-2 border-gray-700 opacity-30'></div>
                      <div className='absolute inset-12 rounded-full border-2 border-gray-700 opacity-30'></div>
                      <div className='absolute inset-16 rounded-full border-2 border-gray-700 opacity-30'></div>

                      {/* Center label */}
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <div className='h-20 w-20 rounded-full bg-gradient-to-br from-[var(--music-gold)] to-[var(--music-gold-dark)] shadow-lg'></div>
                      </div>

                      {/* Gold ring effect when playing */}
                      {isPlaying && (<div className='absolute inset-0 rounded-full border-4 border-[var(--music-gold)] opacity-20 animate-pulse-ring'></div>)}
                    </div>

                    {/* Play/Pause button */}
                    <button onClick={handlePlayPause} className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--music-gold)] shadow-lg transition-all hover:bg-[var(--music-gold-light)] hover:scale-110 active:scale-95' disabled={isLoading}>
                      {isLoading ? (<div className='h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent'></div>) : isPlaying ? (<PauseIcon className='h-7 w-7 text-[var(--music-bg-deep)]'/>) : (<PlayIcon className='h-7 w-7 translate-x-0.5 text-[var(--music-bg-deep)]'/>)}
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className='relative mb-6'>
                  <div onClick={handleSeek} className='group h-2 w-full cursor-pointer overflow-hidden rounded-full bg-white/10 transition-all hover:h-3'>
                    <div className='h-full rounded-full bg-gradient-to-r from-[var(--music-gold-dark)] to-[var(--music-gold-light)] transition-all' style={{ width: `${progress}%` }}></div>
                  </div>
                  <div className='mt-2 flex justify-between text-sm text-[var(--music-gray)]'>
                    <span>{formatTime(audioPosition)}</span>
                    <span>{formatTime(audioDuration)}</span>
                  </div>
                </div>

                {/* Track prompt */}
                <div className='mb-8 rounded-2xl bg-white/5 p-4 backdrop-blur-sm'>
                  <div className='mb-2 text-xs font-medium uppercase tracking-wider text-[var(--music-gold)]'>
                    Generation Prompt
                  </div>
                  <p className='text-sm leading-relaxed text-[var(--music-cream)]'>
                    "{demoTracks[activeDemo].prompt}"
                  </p>
                </div>

                {/* Track List */}
                <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                  {demoTracks.map((track, index) => (<button key={index} onClick={() => handleTrackSelect(index)} className={`group relative overflow-hidden rounded-xl border p-4 text-left transition-all ${activeDemo === index
                ? 'border-[var(--music-gold)] bg-[var(--music-gold)]/10'
                : 'border-white/10 bg-white/5 hover:border-[var(--music-gold)]/50 hover:bg-white/10'}`}>
                      <div className='relative z-10'>
                        <div className='mb-2 flex items-center gap-2'>
                          <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${activeDemo === index && isPlaying
                ? 'bg-[var(--music-gold)]'
                : 'bg-white/10 group-hover:bg-white/20'}`}>
                            {activeDemo === index && isPlaying ? (<div className='flex gap-0.5'>
                                <div className='h-3 w-0.5 animate-pulse bg-[var(--music-bg-deep)]' style={{ animationDelay: '0ms' }}></div>
                                <div className='h-3 w-0.5 animate-pulse bg-[var(--music-bg-deep)]' style={{ animationDelay: '150ms' }}></div>
                                <div className='h-3 w-0.5 animate-pulse bg-[var(--music-bg-deep)]' style={{ animationDelay: '300ms' }}></div>
                              </div>) : (<PlayIcon className='h-4 w-4 text-[var(--music-cream)]'/>)}
                          </div>
                          <span className='text-xs font-medium text-[var(--music-gray)]'>
                            {track.genre}
                          </span>
                        </div>
                        <h4 className='font-display text-lg font-semibold text-[var(--music-cream)]'>
                          {track.name}
                        </h4>
                        <p className='mt-1 text-xs text-[var(--music-gray)]'>
                          {track.duration}
                        </p>
                      </div>
                    </button>))}
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className='mt-12 text-center opacity-0 animate-fade-in-up delay-300'>
              <ButtonLink href='/music-generator' className='inline-flex items-center gap-2 rounded-full border-transparent bg-[var(--music-gold)] px-8 py-4 text-lg font-semibold text-[var(--music-bg-deep)] shadow-lg transition-all hover:bg-[var(--music-gold-light)] hover:shadow-xl hover:scale-105'>
                {user ? t('Create Music') : t('Start Creating for Free')}
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6'/>
                </svg>
              </ButtonLink>
              {!user && (<p className='mt-4 text-sm text-[var(--music-gray)]'>
                  No credit card required
                </p>)}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className='relative px-6 py-24 lg:px-8 lg:py-32'>
          <div className='mx-auto max-w-7xl'>
            <div className='mb-20 text-center'>
              <h2 className='font-display mb-6 text-5xl font-light text-[var(--music-cream)] lg:text-6xl'>
                Professional-grade
                <br />
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  music generation
                </span>
              </h2>
              <p className='mx-auto max-w-2xl text-lg text-[var(--music-gray)]'>
                Every feature you need to create stunning compositions
              </p>
            </div>

            <div className='grid gap-8 lg:grid-cols-2'>
              {/* Feature 1 */}
              <div className='group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-8 transition-all hover:scale-[1.02]'>
                <div className='absolute -top-20 -right-20 h-40 w-40 rounded-full bg-[var(--music-gold)] opacity-5 blur-3xl transition-all group-hover:opacity-10'></div>
                <div className='relative'>
                  <div className='mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--music-gold)]/10'>
                    <svg className='h-7 w-7 text-[var(--music-gold)]' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>
                    </svg>
                  </div>
                  <h3 className='font-display mb-3 text-2xl font-semibold text-[var(--music-cream)]'>
                    AI Composition
                  </h3>
                  <p className='text-[var(--music-gray)]'>
                    Create custom music in any genre from simple text prompts
                    using advanced AI models trained on millions of
                    compositions.
                  </p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className='group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-8 transition-all hover:scale-[1.02]'>
                <div className='absolute -top-20 -right-20 h-40 w-40 rounded-full bg-purple-600 opacity-5 blur-3xl transition-all group-hover:opacity-10'></div>
                <div className='relative'>
                  <div className='mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-600/10'>
                    <svg className='h-7 w-7 text-purple-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z'/>
                    </svg>
                  </div>
                  <h3 className='font-display mb-3 text-2xl font-semibold text-[var(--music-cream)]'>
                    Genre Mastery
                  </h3>
                  <p className='text-[var(--music-gray)]'>
                    Generate authentic music across all genres—from classical
                    symphonies to electronic beats, jazz to rock, and everything
                    between.
                  </p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className='group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-8 transition-all hover:scale-[1.02]'>
                <div className='absolute -top-20 -right-20 h-40 w-40 rounded-full bg-blue-600 opacity-5 blur-3xl transition-all group-hover:opacity-10'></div>
                <div className='relative'>
                  <div className='mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600/10'>
                    <svg className='h-7 w-7 text-blue-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4'/>
                    </svg>
                  </div>
                  <h3 className='font-display mb-3 text-2xl font-semibold text-[var(--music-cream)]'>
                    Fine-Tuned Control
                  </h3>
                  <p className='text-[var(--music-gray)]'>
                    Specify tempo, key, instruments, and mood to craft exactly
                    what you envision. Full creative control at your fingertips.
                  </p>
                </div>
              </div>

              {/* Feature 4 */}
              <div className='group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-8 transition-all hover:scale-[1.02]'>
                <div className='absolute -top-20 -right-20 h-40 w-40 rounded-full bg-green-600 opacity-5 blur-3xl transition-all group-hover:opacity-10'></div>
                <div className='relative'>
                  <div className='mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-green-600/10'>
                    <svg className='h-7 w-7 text-green-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M13 10V3L4 14h7v7l9-11h-7z'/>
                    </svg>
                  </div>
                  <h3 className='font-display mb-3 text-2xl font-semibold text-[var(--music-cream)]'>
                    Studio Quality
                  </h3>
                  <p className='text-[var(--music-gray)]'>
                    Receive high-fidelity, production-ready compositions
                    suitable for commercial use, streaming, and professional
                    projects.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className='relative px-6 py-24 lg:px-8 lg:py-32'>
          <div className='absolute inset-0 bg-gradient-to-b from-transparent via-[var(--music-bg-card)]/50 to-transparent'></div>

          <div className='relative mx-auto max-w-7xl'>
            <div className='mb-20 text-center'>
              <h2 className='font-display mb-6 text-5xl font-light text-[var(--music-cream)] lg:text-6xl'>
                Built for
                <br />
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  every creator
                </span>
              </h2>
            </div>

            <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {[
            {
                title: 'Game Development',
                description: 'Custom soundtracks for any game genre, from puzzle games to racing adventures',
                gradient: 'from-purple-600/20 to-pink-600/20',
            },
            {
                title: 'Video Production',
                description: 'Background music for videos, commercials, and social media content',
                gradient: 'from-blue-600/20 to-cyan-600/20',
            },
            {
                title: 'Podcasts',
                description: 'Intro, outro, and background music for your podcast episodes',
                gradient: 'from-green-600/20 to-emerald-600/20',
            },
            {
                title: 'Mobile Apps',
                description: 'Custom sound design and music to enhance app experiences',
                gradient: 'from-orange-600/20 to-red-600/20',
            },
            {
                title: 'Wellness',
                description: 'Calming ambient music for meditation and mindfulness applications',
                gradient: 'from-indigo-600/20 to-purple-600/20',
            },
            {
                title: 'Creative Projects',
                description: 'Original tracks for art installations, theater, and exhibitions',
                gradient: 'from-pink-600/20 to-rose-600/20',
            },
        ].map((useCase, index) => (<div key={index} className='group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-6 transition-all hover:scale-[1.02]'>
                  <div className={`absolute inset-0 bg-gradient-to-br ${useCase.gradient} opacity-0 transition-opacity group-hover:opacity-100`}></div>
                  <div className='relative'>
                    <h3 className='font-display mb-2 text-xl font-semibold text-[var(--music-cream)]'>
                      {useCase.title}
                    </h3>
                    <p className='text-sm text-[var(--music-gray)]'>
                      {useCase.description}
                    </p>
                  </div>
                </div>))}
            </div>
          </div>
        </section>

        {/* Browse by Genre / Style */}
        <section className='relative px-6 py-24 lg:px-8 lg:py-32'>
          <div className='relative mx-auto max-w-7xl'>
            <div className='mb-12 text-center'>
              <h2 className='font-display mb-6 text-5xl font-light text-[var(--music-cream)] lg:text-6xl'>
                Browse by{' '}
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  genre & style
                </span>
              </h2>
              <p className='mx-auto max-w-2xl text-lg text-[var(--music-gray)]'>
                Explore AI music generation across genres, use cases, and moods
              </p>
            </div>

            {/* Genres */}
            <div className='mb-10'>
              <h3 className='font-display mb-4 text-xl font-semibold text-[var(--music-cream)]'>
                Genres
              </h3>
              <div className='grid gap-3 sm:grid-cols-3 lg:grid-cols-5'>
                {music_subpages
            .filter((s) => s.category === 'genre')
            .map((page) => (<Link key={page.slug} href={`/ai-music-generator/${page.slug}`} className='group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 p-4 text-center transition-all hover:border-[var(--music-gold)]/50 hover:bg-white/10 hover:scale-[1.02]'>
                      <div className={`absolute inset-0 bg-gradient-to-br ${page.gradient} opacity-0 transition-opacity group-hover:opacity-100`}></div>
                      <div className='relative'>
                        <span className='font-display text-lg font-semibold text-[var(--music-cream)]'>
                          {page.name}
                        </span>
                      </div>
                    </Link>))}
              </div>
            </div>

            {/* Use Cases */}
            <div className='mb-10'>
              <h3 className='font-display mb-4 text-xl font-semibold text-[var(--music-cream)]'>
                Use Cases
              </h3>
              <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                {music_subpages
            .filter((s) => s.category === 'use-case')
            .map((page) => (<Link key={page.slug} href={`/ai-music-generator/${page.slug}`} className='group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 p-4 text-center transition-all hover:border-[var(--music-gold)]/50 hover:bg-white/10 hover:scale-[1.02]'>
                      <div className={`absolute inset-0 bg-gradient-to-br ${page.gradient} opacity-0 transition-opacity group-hover:opacity-100`}></div>
                      <div className='relative'>
                        <span className='font-display text-lg font-semibold text-[var(--music-cream)]'>
                          {page.name}
                        </span>
                      </div>
                    </Link>))}
              </div>
            </div>

            {/* Moods */}
            <div>
              <h3 className='font-display mb-4 text-xl font-semibold text-[var(--music-cream)]'>
                Moods
              </h3>
              <div className='grid gap-3 sm:grid-cols-3 lg:grid-cols-5'>
                {music_subpages
            .filter((s) => s.category === 'mood')
            .map((page) => (<Link key={page.slug} href={`/ai-music-generator/${page.slug}`} className='group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 p-4 text-center transition-all hover:border-[var(--music-gold)]/50 hover:bg-white/10 hover:scale-[1.02]'>
                      <div className={`absolute inset-0 bg-gradient-to-br ${page.gradient} opacity-0 transition-opacity group-hover:opacity-100`}></div>
                      <div className='relative'>
                        <span className='font-display text-lg font-semibold text-[var(--music-cream)]'>
                          {page.name}
                        </span>
                      </div>
                    </Link>))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className='relative px-6 py-16 lg:px-8 lg:py-24'>
          <div className='mx-auto max-w-3xl'>
            <div className='mb-12 text-center'>
              <h2 className='font-display mb-4 text-4xl font-light text-[var(--music-cream)] lg:text-5xl'>
                Frequently asked{' '}
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  questions
                </span>
              </h2>
            </div>

            <div className='divide-y divide-white/10' itemScope itemType='https://schema.org/FAQPage'>
              {faqs.map((faq, index) => (<Disclosure as='div' key={index} className='py-4' itemScope itemProp='mainEntity' itemType='https://schema.org/Question'>
                  {({ open }) => (<>
                      <Disclosure.Button className='flex w-full items-start justify-between text-left'>
                        <span className='text-lg font-medium text-[var(--music-cream)]' itemProp='name'>
                          {faq.question}
                        </span>
                        <ChevronDownIcon className={clsxm(open ? '-rotate-180' : 'rotate-0', 'ml-4 h-5 w-5 flex-shrink-0 text-[var(--music-gold)] transition-transform')}/>
                      </Disclosure.Button>
                      <Disclosure.Panel as='div' className='mt-3 pr-12' itemScope itemProp='acceptedAnswer' unmount={false} itemType='https://schema.org/Answer'>
                        <p className='text-[var(--music-gray)]' itemProp='text'>
                          {faq.answer}
                        </p>
                      </Disclosure.Panel>
                    </>)}
                </Disclosure>))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className='relative px-6 py-32 lg:px-8'>
          <div className='mx-auto max-w-4xl text-center'>
            <h2 className='font-display mb-6 text-5xl font-light text-[var(--music-cream)] lg:text-7xl'>
              Ready to compose
              <br />
              <span className='font-semibold italic text-[var(--music-gold)]'>
                your masterpiece?
              </span>
            </h2>
            <p className='mb-10 text-xl text-[var(--music-gray)]'>
              Join thousands of creators bringing their projects to life with
              AI-generated music
            </p>
            <div className='flex flex-col items-center gap-4 sm:flex-row sm:justify-center'>
              <ButtonLink href='/music-generator' className='inline-flex items-center gap-2 rounded-full border-transparent bg-[var(--music-gold)] px-8 py-4 text-lg font-semibold text-[var(--music-bg-deep)] shadow-lg transition-all hover:bg-[var(--music-gold-light)] hover:shadow-xl hover:scale-105'>
                {user ? t('Create Music') : t('Get Started for Free')}
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6'/>
                </svg>
              </ButtonLink>
              {!user && (<Link href='/login?preset=music' className='inline-flex items-center gap-2 text-lg font-medium text-[var(--music-gray)] transition-colors hover:text-[var(--music-cream)]'>
                  Log in
                  <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6'/>
                  </svg>
                </Link>)}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>);
}

    async function __Next_Translate__getStaticProps__1a1052e9155__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-music-generator/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1052e9155__ as getStaticProps }
  